// src/components/admin/tabs/MachinesTab.js
import React, { useState, useContext, useEffect } from "react";
import { Card, Button, Table, Modal, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { DashboardContext } from "../../../contexts/AdminContext";
import MachineForm from "../forms/MachineForm";
import { popup } from "../../common/popup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./css/MachinesTab.css";

const MachinesTab = () => {
  const {
    machines,
    sectors,
    loading,
    deleteMachine,
    fetchMachines,
    fetchMachinesBySector,
  } = useContext(DashboardContext);

  const [showMachineModal, setShowMachineModal] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [machineToDelete, setMachineToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  /* =========================
     FILTER STATE (SAMA KAYAK USER)
  ========================= */
  const [selectedSector, setSelectedSector] = useState("all");
  const filteredMachines = machines.filter((machine) => {
    const keyword = searchTerm.toLowerCase();

    return (
      machine.name?.toLowerCase().includes(keyword) ||
      machine.code?.toLowerCase().includes(keyword) ||
      machine.sector?.name?.toLowerCase().includes(keyword)
    );
  });

  /* =========================
     INITIAL LOAD
  ========================= */
  useEffect(() => {
    fetchMachines();
  }, [fetchMachines]);

  /* =========================
     CRUD HANDLER
  ========================= */
  const handleAddClick = () => {
    setEditingMachine(null);
    setShowMachineModal(true);
  };

  const handleEditClick = (machine) => {
    setEditingMachine(machine);
    setShowMachineModal(true);
  };

  const handleDeleteClick = (machine) => {
    setMachineToDelete(machine);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!machineToDelete?.id) return;
    const result = await deleteMachine(machineToDelete.id);
    if (result?.success) {
      setShowDeleteModal(false);
      setMachineToDelete(null);
      popup.success("Data berhasil dihapus");
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case "Operational":
        return "Beroperasi";
      case "Need Maintenance":
        return "Butuh Perbaikan";
      case "Under Maintenance":
        return "Sedang Diperbaiki";
      case "Down":
        return "Tidak Aktif";
      default:
        return status || "-";
    }
  };

  /* =========================
     EXPORT EXCEL (mengikuti filter & pencarian aktif)
  ========================= */
  const handleExportExcel = () => {
    if (!filteredMachines.length) {
      popup.alert("Tidak ada data mesin untuk diekspor.");
      return;
    }

    const data = filteredMachines.map((m, i) => ({
      No: i + 1,
      Kode: m.code || "-",
      Mesin: m.name || "-",
      Sektor:
        m.sector?.name ||
        sectors.find((s) => s.id === m.sector_id)?.name ||
        "-",
      "Tahun Aktif": m.activation_date
        ? new Date(m.activation_date).toLocaleDateString("id-ID")
        : "-",
      Keterangan: m.description || "-",
      "Jumlah Tools": m.tools?.length || 0,
      Tools: m.tools?.length
        ? m.tools.map((t) => t.part_name).join(", ")
        : "-",
      Status: translateStatus(m.status),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data Mesin");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `data_mesin_${Date.now()}.xlsx`);
  };

  return (
    <>
      <Card style={{ marginTop: "-24px" }}>
        <Card.Header className="machine-card-header">
          <div className="d-flex justify-content-between align-items-center">
            <span className="" style={{ fontSize: "28px" }}>
              Manajemen Mesin
            </span>

            <div className="d-flex justify-content-between align-items-center gap-2">
              {/* 🔽 FILTER SEKTOR */}
              <Form.Select
                value={selectedSector}
                className="form-select-sm"
                disabled={loading}
                onChange={async (e) => {
                  const value = e.target.value;
                  setSelectedSector(value);

                  if (value === "all") {
                    await fetchMachines();
                  } else {
                    await fetchMachinesBySector(value);
                  }
                }}
              >
                <option value="all">Semua Sektor</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                type="text"
                placeholder="🔍 Cari..."
                size="sm"
                className="me-2"
                style={{ width: "240px" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {loading && <Spinner size="sm" animation="border" />}

              <Button
                variant="success"
                size="sm"
                style={{ padding: "8px", whiteSpace: "nowrap" }}
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>

              <Button
                variant="primary"
                size="sm"
                className="btn-add-machine"
                style={{ padding: "8px" }}
                onClick={() => {
                  if (!sectors.length) {
                    popup.alert(
                      "Silakan buat sektor terlebih dahulu sebelum menambahkan mesin.",
                    );
                    return;
                  }
                  handleAddClick();
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Tambah Mesin</span>
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body
          className="card-body-scroll"
          style={{ padding: "10px", height: "80vh" }}
        >
          <div className="table-scroll" style={{}}>
            <Table striped bordered hover className="machine-table">
              <thead>
                <tr>
                  <th
                    className="text-center"
                    style={{ background: "#1e3a8a", color: "#fff" }}
                  >
                    No
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>Kode</th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Mesin
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Sektor
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Tahun Aktif
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Keterangan
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Tools
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Status
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>Opsi</th>
                </tr>
              </thead>
              <tbody>
                {filteredMachines.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Tidak ada mesin ditemukan
                    </td>
                  </tr>
                ) : (
                  filteredMachines.map((machine, index) => (
                    <tr key={machine.id}>
                      <td className="text-center">{index + 1}</td>
                      <td>{machine.code || "-"}</td>
                      <td className="cell-left">{machine.name || "-"}</td>
                      <td>
                        {machine.sector?.name ||
                          sectors.find((s) => s.id === machine.sector_id)
                            ?.name ||
                          "-"}
                      </td>
                      <td>
                        {machine.activation_date
                          ? new Date(
                              machine.activation_date,
                            ).toLocaleDateString("id-ID")
                          : "-"}
                      </td>
                      <td style={{ maxWidth: "200px" }}>
                        {machine.description || "-"}
                      </td>

                      <td className="cell-left" style={{ maxWidth: "240px" }}>
                        {machine.tools && machine.tools.length > 0 ? (
                          <span title={machine.tools
                            .map((t) => t.part_name)
                            .join(", ")}>
                            <strong>{machine.tools.length}</strong>:{" "}
                            {machine.tools.map((t) => t.part_name).join(", ")}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td>
                        <span
                          className={`status-badge status-${machine.status
                            ?.toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {translateStatus(machine.status)}
                        </span>
                      </td>
                      <td className="d-flex">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditClick(machine)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(machine)}
                        >
                          Hapus
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <MachineForm
        show={showMachineModal}
        handleClose={() => setShowMachineModal(false)}
        initialData={editingMachine}
        onSuccess={async () => {
          await fetchMachines(); // ⬅️ INI KUNCI
          setShowMachineModal(false);
        }}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Hapus</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Apakah Anda yakin ingin menghapus mesin "{machineToDelete?.name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MachinesTab;
